import React, { useState, useRef, useEffect } from 'react';
import './DropdownPadrao.css';
import CampoObrigatorio from '../CampoObrigatorio/CampoObrigatorio';
import { IoMdArrowDropdown } from "react-icons/io";
import { MdClose } from 'react-icons/md';
import InputPadrao from '../InputPadrao/InputPadrao';
import CheckboxPadrao from '../CheckboxPadrao/CheckboxPadrao';
import Mensagens from '../../utils/MensagemUtils';
import { validarPermissoes } from '../../utils/PermissaoUtils';

const DropdownPadrao = ({
  id,
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Selecione uma opção',
  required = false,
  disabled = false,
  erro = false,
  showFilter = false,
  erroMensagem = '',
  permissao,
  exibirSe = true,
  retornarObjeto = false, 
  mapearOpcao = null,
  multiple = false,
  cresce100 = false,
  children, // Suporte para children como label
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(multiple ? (Array.isArray(value) ? value : []) : (value || ''));
  const [isFocused, setIsFocused] = useState(false);
  const [filter, setFilter] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const optionsRefs = useRef([]);
  const filterInputRef = useRef(null);
  const dropdownTriggerRef = useRef(null);
  const wasManuallyOpened = useRef(false);

  useEffect(() => {
    if (multiple) {
      setSelected(Array.isArray(value) ? value : []);
    } else {
      // Se for string vazia ou null/undefined, garante que selected seja vazio
      if (value === '' || value === null || value === undefined) {
        setSelected('');
      } else if (retornarObjeto && typeof value === 'object') {
        // Se for objeto vazio, também define como vazio
        const isEmptyObject = !value || Object.keys(value).length === 0;
        setSelected(isEmptyObject ? '' : value);
      } else {
        setSelected(value);
      }
    }
  }, [value, multiple, retornarObjeto]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setIsFocused(false);
        setFilter(''); // Limpar o filtro quando fechar o dropdown
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setFocusedOptionIndex(-1);

    if (open && showFilter && filterInputRef.current) {
      setTimeout(() => {
        filterInputRef.current.focus();
      }, 0);
    }
  }, [open, showFilter]);

  useEffect(() => {
    if (!open && wasManuallyOpened.current && dropdownTriggerRef.current) {
      setTimeout(() => {
        dropdownTriggerRef.current.focus();
      }, 0);
    }
  }, [open]);

  // Efeito para limpar o filtro quando o dropdown for fechado
  useEffect(() => {
    if (!open) {
      setFilter('');
    }
  }, [open]);

  const handleSelect = option => {
    if (multiple) {
      // Para seleção múltipla
      let newSelected = [...(selected || [])];
      
      if (newSelected.includes(option.value)) {
        // Se já estiver selecionado, remove
        newSelected = newSelected.filter(val => val !== option.value);
      } else {
        // Caso contrário, adiciona
        newSelected.push(option.value);
      }
      
      setSelected(newSelected);
      
      if (onChange) {
        onChange({
          target: {
            value: newSelected,
            id
          }
        });
      }
    } else {
      // Para seleção única
      setSelected(option.value);
      setOpen(false);
      setIsFocused(false);
      
      if (onChange) {
        if (retornarObjeto) {
          onChange(option); 
        } else {
          onChange({ target: { value: option.value, id } });
        }
      }
    }
  };

  const handleClear = e => {
    e.stopPropagation();
    if (multiple) {
      setSelected([]);
      if (onChange) onChange({ target: { value: [], id } });
    } else {
      setSelected('');
      setOpen(false);
      setIsFocused(false);
      setFilter(''); // Limpar o filtro ao limpar a seleção
      
      // Correção para tratar também o caso de retornarObjeto
      if (onChange) {
        if (retornarObjeto) {
          // Para componentes que esperam um objeto, enviamos string vazia
          // e garantimos que value não tenha uma referência ao objeto anterior
          onChange(''); 
        } else {
          onChange({ target: { value: '', id } });
        }
      }
    }
  };

  const handleFilter = e => {
    const valor = e.target.value;
    // Só aplica toLowerCase se não for objeto
    setFilter(typeof valor === 'string' ? valor.toLowerCase() : valor);
  };

  const handleKeyDown = e => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (!open) {
          e.preventDefault();
          setOpen(true);
          setIsFocused(true);
          wasManuallyOpened.current = true;
        }
        break;
      case 'ArrowDown':
        if (open) {
          e.preventDefault();
          if (showFilter && filterInputRef.current) {
            filterInputRef.current.focus();
          } else if (optionsRefs.current.length > 0) {
            optionsRefs.current[0].focus();
            setFocusedOptionIndex(0);
          }
        } else {
          e.preventDefault();
          setOpen(true);
          setIsFocused(true);
          wasManuallyOpened.current = true;
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
          setFilter(''); // Limpar o filtro ao fechar com Escape
        }
        break;
      case 'Tab':
        break;
      default:
        break;
    }
  };

  const handleOptionKeyDown = (e, option, index) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleSelect(option);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (index < filteredOptions.length - 1) {
          setFocusedOptionIndex(index + 1);
          optionsRefs.current[index + 1].focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (index > 0) {
          setFocusedOptionIndex(index - 1);
          optionsRefs.current[index - 1].focus();
        } else if (showFilter && filterInputRef.current) {
          filterInputRef.current.focus();
          setFocusedOptionIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setFilter(''); // Limpar o filtro ao fechar com Escape nas opções
        break;
      case 'Tab':
        break;
      default:
        break;
    }
  };

  const handleFilterKeyDown = e => {
    if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
      e.preventDefault();
      optionsRefs.current[0].focus();
      setFocusedOptionIndex(0);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setFilter(''); // Limpar o filtro ao fechar com Escape no campo de filtro
    }
  };

  // Verifica se o dropdown está preenchido, considerando diferentes tipos de valor
  const isPreenchido = multiple 
    ? (selected && selected.length > 0) 
    : (!!selected || (retornarObjeto && value && typeof value === 'object' && Object.keys(value).length > 0));
  const hasError = erroMensagem || erro;

  // Função para verificar se uma opção está selecionada
  const isOptionSelected = (option) => {
    if (multiple) {
      return selected && selected.includes(option.value);
    } else {
      return option.value === selected;
    }
  };

  // Novo: mapeia o value selecionado se necessário
  let selectedOption = null;
  if (mapearOpcao && typeof mapearOpcao === 'function' && value && typeof value === 'object') {
    selectedOption = mapearOpcao(value);
  } else if (Array.isArray(options) && !multiple) {
    selectedOption = options.find(opt => {
      if (mapearOpcao && typeof mapearOpcao === 'function') {
        const mapped = mapearOpcao(opt);
        return mapped.value === value;
      }
      return opt.value === value;
    });
    if (mapearOpcao && typeof mapearOpcao === 'function' && selectedOption) {
      selectedOption = mapearOpcao(selectedOption);
    }
  }

  const filteredOptions = showFilter
    ? options
        .map(opt => {
          if (mapearOpcao && typeof mapearOpcao === 'function') {
            return mapearOpcao(opt);
          }
          return opt;
        })
        .filter(opt => {
          if (!opt.label || typeof opt.label !== 'string') return false;
          if (typeof filter !== 'string') return true;
          return opt.label.toLowerCase().includes(filter.toLowerCase());
        })
    : options;

  useEffect(() => {
    optionsRefs.current = optionsRefs.current.slice(0, filteredOptions.length);
  }, [filteredOptions]);

  // Função para obter o texto a ser exibido no dropdown
  const getDisplayText = () => {
    if (multiple) {
      if (!selected || selected.length === 0) {
        return placeholder;
      }
      
      // Para seleção múltipla, mostra "Total selecionado (X)"
      return `Total selecionado (${selected.length})`;
    } else {
      if (!selectedOption) {
        return placeholder;
      }
      
      return selectedOption.label;
    }
  };
  
  // NOVO: se exibirSe for false, não renderiza o componente
  if (exibirSe === false) return null;

  const hasPermissao = permissao ? validarPermissoes(permissao) : true;
  if (!hasPermissao) return null;

  return (
    <div className={`input-container ${cresce100 ? 'dropdown-cresce100' : ''}`} ref={dropdownRef}>
      <div
        className={`input-wrapper ${isPreenchido ? 'preenchido' : ''} ${
          hasError ? 'erro' : ''
        } ${isFocused ? 'focused' : ''}`}
      >
        <div
          className={`dropdown-padrao ${disabled ? 'disabled' : ''}`}
          onClick={() => {
            if (!disabled) {
              const newOpenState = !open;
              setOpen(newOpenState);
              setIsFocused(true);
              if (newOpenState) {
                setFilter(''); // Limpar o filtro ao abrir o dropdown
              }
              wasManuallyOpened.current = true;
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={e => {
            if (!dropdownRef.current?.contains(e.relatedTarget)) {
              setIsFocused(false);
            }
          }}
          id={id}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? `${id}-options` : undefined}
          ref={dropdownTriggerRef}
        >
          <span className={`dropdown-value ${isPreenchido ? 'show' : 'placeholder'}`}>
            {isPreenchido 
              ? (multiple 
                  ? `Total selecionado (${selected.length})` 
                  : ((selectedOption && selectedOption.label) || 
                     (retornarObjeto && value && typeof value === 'object' && value.label) || 
                     ''))
              : placeholder}
          </span>
          {isPreenchido && !disabled && (
            <div className="limpar-dropdown" onClick={handleClear} title="Limpar" tabIndex={-1}>
              <MdClose />
            </div>
          )}
          <span className={`dropdown-arrow${open ? ' open' : ''}`}>
            <IoMdArrowDropdown />
          </span>
        </div>

        {label && (
          <div className="input-label-container">
            <label htmlFor={id} className="input-label">
              {label}
            </label>
            {required && (
              <span className="required-spacer">
                <CampoObrigatorio />
              </span>
            )}
          </div>
        )}

        {open && (
          <div className="dropdown-options-box">
            <ul className="dropdown-list" role="listbox" id={`${id}-options`}>
              {showFilter && (
                <div className="dropdown-filter-box">
                  <InputPadrao
                    placeholder={'Escreva algo.'}
                    onChange={handleFilter}
                    onKeyDown={handleFilterKeyDown}
                    ref={filterInputRef}
                    tabIndex={0}
                    value={filter || ''} // Garantir que o campo de entrada mostre o valor atual do filtro
                  />
                </div>
              )}

              {multiple && filteredOptions.length > 0 && (
                <li className={`dropdown-option dropdown-option-select-all`}>
                  <CheckboxPadrao
                    label="Selecionar todos"
                    labelStyle="capitalize"
                    checked={filteredOptions.every(opt => selected.includes(opt.value))}
                    onChange={() => {
                      const allValues = filteredOptions.map(opt => opt.value);
                      const allSelected = filteredOptions.every(opt => selected.includes(opt.value));
                      
                      const newValue = allSelected ? [] : allValues;
                      setSelected(newValue);
                      if (onChange) {
                        onChange({
                          target: {
                            value: newValue,
                            id
                          }
                        });
                      }
                    }}
                  />
                </li>
              )}

              {filteredOptions.map((option, index) => (
                <li
                  key={option.value}
                  className={`dropdown-option${
                    (multiple ? isOptionSelected(option) : (selectedOption && option.value === selectedOption.value)) ? ' selected' : ''
                  }${focusedOptionIndex === index ? ' focused' : ''}`}
                  onClick={multiple ? undefined : () => handleSelect(option)}
                  onKeyDown={e => handleOptionKeyDown(e, option, index)}
                  title={option.label}
                  tabIndex={0}
                  ref={el => (optionsRefs.current[index] = el)}
                  role="option"
                  aria-selected={(multiple ? isOptionSelected(option) : (selectedOption && option.value === selectedOption.value))}
                >
                  {multiple ? (
                    <CheckboxPadrao
                      label={option.label}
                      labelStyle="capitalize"
                      checked={isOptionSelected(option)}
                      onChange={() => handleSelect(option)}
                      className="dropdown-option-checkbox-label"
                    />
                  ) : (
                    <div className="dropdown-option-text-container">
                      <span className="dropdown-option-text dropdown-option-text-capitalize">{option.label}</span>
                    </div>
                  )}
                </li>
              ))}

              {filteredOptions.length === 0 && (
                <li className="dropdown-option disabled" role="option">
                  {Mensagens.NENHUM_ITEM_ENCONTRADO}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
      {erroMensagem && <div className="input-erro-msg">{erroMensagem}</div>}
    </div>
  );
};

export default DropdownPadrao;